import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Code,
  Smartphone,
  Bot,
  Workflow,
  Database,
  Server,
  ArrowRight,
  Zap,
  Globe,
  Cpu,
} from "lucide-react";

const ServicesSection = () => {
  // State to track which service card is expanded
  const [expandedService, setExpandedService] = useState(null);

  // Services data organized by category
  const services = [
    // Web Development
    {
      id: "web-dev",
      title: "Web Development",
      icon: <Globe className="w-8 h-8" />,
      primaryColor: "blue",
      description:
        "Modern, responsive web applications using the latest technologies and frameworks",
      features: [
        "Responsive and performant front-end",
        "Secure and scalable back-end APIs",
        "Database design and optimization",
        "SEO and accessibility compliance",
      ],
      technologies: [
        "React",
        "Angular",
        "Vue.js",
        "Node.js",
        "Laravel",
        "FastAPI",
      ],
    },
    // Mobile Development
    {
      id: "mobile-dev",
      title: "Mobile Development",
      icon: <Smartphone className="w-8 h-8" />,
      primaryColor: "green",
      description:
        "Native and cross-platform mobile applications for iOS and Android devices",
      features: [
        "Cross-platform development (React Native)",
        "Native app development",
        "Offline functionality",
        "Push notifications and real-time updates",
      ],
      technologies: [
        "React Native",
        "Flutter",
        "Swift",
        "Kotlin",
        "Firebase",
        "Expo",
      ],
    },
    // AI & Automation
    {
      id: "automation",
      title: "Business Automation",
      icon: <Workflow className="w-8 h-8" />,
      primaryColor: "purple",
      description:
        "Streamlined workflows and optimized processes for businesses across industries",
      features: [
        "Custom workflow automation",
        "Data processing pipelines",
        "Real-time notifications",
        "Integration with existing tools",
      ],
      technologies: [
        "Python",
        "AWS Lambda",
        "Kafka",
        "RabbitMQ",
        "Supabase",
        "Firebase",
      ],
    },
    // AI Agents
    {
      id: "ai-agents",
      title: "AI Agents",
      icon: <Bot className="w-8 h-8" />,
      primaryColor: "indigo",
      description:
        "Intelligent assistants that interact naturally with users while autonomously handling complex tasks",
      features: [
        "Conversational AI for natural interaction",
        "Domain-specific knowledge bases",
        "Autonomous decision-making capabilities",
        "Seamless integration with existing systems",
      ],
      technologies: [
        "LangChain",
        "OpenAI API",
        "RAG",
        "Vector Databases",
        "AWS AI Services",
      ],
    },
  ];

  // Helper function to get color classes based on service's primary color
  const getColorClasses = (color, type) => {
    const colorMap = {
      blue: {
        bg: "bg-blue-100",
        bgHover: "hover:bg-blue-200",
        text: "text-blue-600",
        border: "border-blue-200",
        gradient: "from-blue-500 to-sky-400",
      },
      green: {
        bg: "bg-emerald-100",
        bgHover: "hover:bg-emerald-200",
        text: "text-emerald-600",
        border: "border-emerald-200",
        gradient: "from-emerald-500 to-green-400",
      },
      purple: {
        bg: "bg-purple-100",
        bgHover: "hover:bg-purple-200",
        text: "text-purple-600",
        border: "border-purple-200",
        gradient: "from-purple-500 to-fuchsia-400",
      },
      indigo: {
        bg: "bg-indigo-100",
        bgHover: "hover:bg-indigo-200",
        text: "text-indigo-600",
        border: "border-indigo-200",
        gradient: "from-indigo-500 to-blue-400",
      },
    };

    return colorMap[color][type];
  };

  return (
    <section id="services" className="py-24 mt-16 mb-16 bg-white rounded-2xl">
      <div className="w-full max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-2 font-mono">
              Services
            </h2>
            <div className="w-16 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="max-w-2xl mx-auto text-gray-600">
              Comprehensive technology solutions tailored to your business
              needs, from intelligent AI agents to scalable web and mobile
              applications.
            </p>
          </motion.div>
        </div>

        {/* Services Grid - Hexagonal Layout */}
        <div className="relative mb-16">
          {/* Creative Decorative Elements */}
          <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-blue-50 filter blur-3xl opacity-50"></div>
          <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-purple-50 filter blur-3xl opacity-50"></div>

          {/* Connection lines between services */}
          <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
            <path
              d="M200,200 C300,100 500,150 600,200"
              stroke="#e5e7eb"
              strokeWidth="1"
              fill="none"
              strokeDasharray="5,5"
            />
            <path
              d="M200,350 C350,400 450,400 600,350"
              stroke="#e5e7eb"
              strokeWidth="1"
              fill="none"
              strokeDasharray="5,5"
            />
            <path
              d="M200,200 C250,300 200,350 200,400"
              stroke="#e5e7eb"
              strokeWidth="1"
              fill="none"
              strokeDasharray="5,5"
            />
            <path
              d="M600,200 C550,300 600,350 600,400"
              stroke="#e5e7eb"
              strokeWidth="1"
              fill="none"
              strokeDasharray="5,5"
            />
          </svg>

          {/* Main Services Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden ${
                  expandedService === service.id
                    ? "bg-gradient-to-br " +
                      getColorClasses(service.primaryColor, "gradient")
                    : "bg-white"
                }`}
              >
                <div
                  className="p-8 h-full flex flex-col cursor-pointer"
                  onClick={() =>
                    setExpandedService(
                      expandedService === service.id ? null : service.id
                    )
                  }
                >
                  {/* Service Header */}
                  <div className="flex items-start mb-6">
                    <div
                      className={`p-3 rounded-xl ${getColorClasses(
                        service.primaryColor,
                        "bg"
                      )} mr-4`}
                    >
                      <div
                        className={getColorClasses(
                          service.primaryColor,
                          "text"
                        )}
                      >
                        {service.icon}
                      </div>
                    </div>

                    <div>
                      <h3
                        className={`text-xl font-bold font-mono mb-1 ${
                          expandedService === service.id
                            ? "text-white"
                            : "text-gray-800"
                        }`}
                      >
                        {service.title}
                      </h3>
                      <p
                        className={`text-sm ${
                          expandedService === service.id
                            ? "text-white text-opacity-90"
                            : "text-gray-500"
                        }`}
                      >
                        {service.description}
                      </p>
                    </div>
                  </div>

                  {/* Service Content */}
                  <div
                    className={`mt-2 transition-all duration-300 ${
                      expandedService === service.id
                        ? "opacity-100"
                        : "opacity-70"
                    }`}
                  >
                    <h4
                      className={`text-sm uppercase tracking-wider mb-3 font-medium ${
                        expandedService === service.id
                          ? "text-white text-opacity-90"
                          : "text-gray-500"
                      }`}
                    >
                      Key Features
                    </h4>

                    <ul className="space-y-2 mb-4">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-start">
                          <div
                            className={`mr-2 mt-1 ${
                              expandedService === service.id
                                ? "text-white"
                                : getColorClasses(service.primaryColor, "text")
                            }`}
                          >
                            <Zap className="w-4 h-4" />
                          </div>
                          <span
                            className={
                              expandedService === service.id
                                ? "text-white"
                                : "text-gray-600"
                            }
                          >
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* Technologies Tags */}
                    <div className="flex flex-wrap gap-2 mt-4">
                      {service.technologies.map((tech, i) => (
                        <span
                          key={i}
                          className={`px-2 py-1 text-xs rounded-full ${
                            expandedService === service.id
                              ? "bg-white bg-opacity-20 text-white"
                              : `${getColorClasses(
                                  service.primaryColor,
                                  "bg"
                                )} ${getColorClasses(
                                  service.primaryColor,
                                  "text"
                                )}`
                          }`}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    {/* Learn More Link */}
                    <div className="mt-6 flex justify-end">
                      <a
                        href={`#${service.id}-details`}
                        className={`inline-flex items-center text-sm font-medium transition-colors ${
                          expandedService === service.id
                            ? "text-white"
                            : getColorClasses(service.primaryColor, "text")
                        }`}
                      >
                        Learn more
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* AAAGentive Featured Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-16 rounded-2xl overflow-hidden border border-gray-100"
        >
          <div className="relative">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-blue-500"></div>

            {/* Background pattern */}
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='white' fill-opacity='1' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E\")",
              }}
            ></div>

            <div className="relative p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                <div className="md:col-span-2">
                  <div className="flex items-center mb-6">
                    <div className="bg-white rounded-xl p-2 mr-4">
                      <Bot className="w-8 h-8 text-indigo-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-white font-mono">
                      AAAGentive
                    </h3>
                  </div>

                  <p className="text-white text-opacity-90 mb-6">
                    AAAGentive is my specialized AI and automation agency
                    creating intelligent solutions for real estate and
                    hospitality businesses across South Africa. We develop
                    conversational AI agents and custom automation workflows
                    that streamline operations, enhance customer experiences,
                    and drive efficiency.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div className="bg-white bg-opacity-10 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <Cpu className="w-5 h-5 text-white mr-2" />
                        <h4 className="text-white font-semibold">AI Agents</h4>
                      </div>
                      <p className="text-white text-opacity-80 text-sm">
                        Conversational AI agents that understand
                        industry-specific context and provide 24/7 assistance.
                      </p>
                    </div>

                    <div className="bg-white bg-opacity-10 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <Server className="w-5 h-5 text-white mr-2" />
                        <h4 className="text-white font-semibold">
                          Custom Workflows
                        </h4>
                      </div>
                      <p className="text-white text-opacity-80 text-sm">
                        Tailored automation workflows that integrate with your
                        existing systems and processes.
                      </p>
                    </div>
                  </div>

                  <a
                    href="https://aaagentive.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-white text-indigo-600 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
                  >
                    Visit AAAGentive
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </div>

                <div className="hidden md:block relative">
                  <div className="aspect-square relative">
                    {/* Abstract representation of AI agent */}
                    <div className="absolute inset-0 bg-white bg-opacity-10 rounded-full animate-pulse-slow"></div>
                    <div className="absolute inset-8 bg-white bg-opacity-20 rounded-full animate-pulse-slower"></div>
                    <div className="absolute inset-16 bg-white bg-opacity-30 rounded-full"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-white">
                        <svg
                          className="w-20 h-20 opacity-80"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                            fill="currentColor"
                          />
                          <path
                            d="M8 14C9.10457 14 10 13.1046 10 12C10 10.8954 9.10457 10 8 10C6.89543 10 6 10.8954 6 12C6 13.1046 6.89543 14 8 14Z"
                            fill="currentColor"
                          />
                          <path
                            d="M16 14C17.1046 14 18 13.1046 18 12C18 10.8954 17.1046 10 16 10C14.8954 10 14 10.8954 14 12C14 13.1046 14.8954 14 16 14Z"
                            fill="currentColor"
                          />
                          <path
                            d="M12 17C14.76 17 17 14.76 17 12H7C7 14.76 9.24 17 12 17Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* Connection lines */}
                    <svg className="absolute inset-0" viewBox="0 0 100 100">
                      <line
                        x1="50"
                        y1="20"
                        x2="80"
                        y2="50"
                        stroke="white"
                        strokeWidth="0.5"
                        strokeDasharray="2,2"
                      />
                      <line
                        x1="50"
                        y1="20"
                        x2="20"
                        y2="50"
                        stroke="white"
                        strokeWidth="0.5"
                        strokeDasharray="2,2"
                      />
                      <line
                        x1="50"
                        y1="20"
                        x2="50"
                        y2="80"
                        stroke="white"
                        strokeWidth="0.5"
                        strokeDasharray="2,2"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <a
            href="#contact"
            className="inline-flex items-center px-8 py-4 rounded-lg font-medium text-lg transition-colors bg-black text-white hover:bg-gray-800"
          >
            <span>Get in Touch</span>
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
          <p className="text-gray-500 text-sm mt-4">
            Let's discuss how I can help with your next project
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
